import { safeFetch } from '../helpers'
import { wstethFetcher } from './fetchers/lido'
import { createWeethFetcher } from './fetchers/etherfi'
import { INTRINSIC_FETCHERS } from './fetcherRegistry'
import { propagateReceiptYields } from './receipts'

export {
  INTRINSIC_FETCHERS,
  LENDING_ONLY_FETCHERS,
  collectVaultFetchers,
} from './fetcherRegistry'

/**
 * Fetch all intrinsic yields and merge into a single map keyed by asset
 * group (`"Display Name::SYMBOL"`), value = APR percent.
 *
 * Runs the single canonical `INTRINSIC_FETCHERS` set (see
 * `fetcherRegistry.ts`), which is the union of every vault-registry
 * fetcher and the lending-only extras — so this map and the LST/savings
 * vault providers always agree.
 *
 * Two fetchers are driven manually rather than from the array:
 *  - `wstETH` runs first because `weETH` composes off its result
 *    (EtherFi APR = wstETH APR + LRT2 bonus), and we must not fetch it
 *    twice — it is filtered out of the array run below.
 *  - `weETH` is composed via `createWeethFetcher` and has no plain array
 *    entry.
 */
export async function fetchIntrinsicYields(): Promise<{
  [asset: string]: number | undefined
}> {
  // Start wsteth first (weeth depends on it) but don't await yet.
  const wstethPromise = safeFetch(wstethFetcher.label, wstethFetcher.fetch)

  // weeth internally awaits wstethPromise — runs in parallel with everything
  // else.
  const weethFetcher = createWeethFetcher(wstethPromise)

  // Canonical union minus wstETH (run once as the promise above; weETH is
  // composed separately and is not part of the array).
  const rest = INTRINSIC_FETCHERS.filter((f) => f !== wstethFetcher)

  const results = await Promise.all([
    wstethPromise,
    safeFetch(weethFetcher.label, weethFetcher.fetch),
    ...rest.map((f) => safeFetch(f.label, f.fetch)),
  ])

  const intrinsicYields: { [asset: string]: number | undefined } = {}
  for (const result of results) {
    if (result) Object.assign(intrinsicYields, result)
  }
  // Receipts (Dolomite dGM/dsavETH …) inherit their underlying's rate under
  // their own group — the key the origin's per-group table joins on.
  await propagateReceiptYields(intrinsicYields)
  return intrinsicYields
}
